import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Navigate } from '@sorare/routing';
import { So5LeaderboardType } from '@sorare/core/src/__generated__/globalTypes';
import {
  ANY_SPORT_HOME,
  ANY_SPORT_PLAY_BOARDS,
  ANY_SPORT_SERIES,
  FOOTBALL_ONBOARDING,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLoggedIn } from '@sorare/core/src/hooks/auth/useIsLoggedIn';
import { useEnableSorare27Nav } from '@sorare/core/src/hooks/useEnableSorare27Nav';
import { WalletLanding } from '@sorare/gameplay/src/components/landing/WalletLanding/index.tsx';

import { FAQ } from '#src/components/landing/faq/index.tsx';

const messages = defineMessages({
  metadatasTitle: {
    defaultMessage:
      'Play fantasy football with ownable digital player cards - Sorare',
  },
  metadatasDescription: {
    defaultMessage:
      "Play Sorare's free fantasy football game. Collect, buy, sell & compete with ownable digital player cards from the world's top leagues to win cash prizes.",
  },
});

type Props = {
  isRoot?: boolean;
};
const FootballPage = ({ isRoot }: Props) => {
  const { generateSportContextPath } = useSportContext();
  const isLoggedIn = useIsLoggedIn();
  const enableSorare27Nav = useEnableSorare27Nav();
  const { formatMessage } = useIntl();
  const { setPageMetadata } = useSeoContext();
  const { currentUser: contextCurrentUser, footballUserProfile } =
    useCurrentUserContext();

  useEffect(
    () =>
      setPageMetadata(formatMessage(messages.metadatasTitle), {
        img: 'https://frontend-assets.sorare.com/meta/social-picture-football-2027.jpg',
        description: formatMessage(messages.metadatasDescription),
      }),
    [setPageMetadata, formatMessage]
  );
  if (isLoggedIn) {
    if (
      (contextCurrentUser?.forcedIntoOnboarding ||
        footballUserProfile?.forceToProOnboarding) &&
      !footballUserProfile?.onboarded
    ) {
      return <Navigate to={FOOTBALL_ONBOARDING} replace />;
    }
    if (
      contextCurrentUser &&
      !contextCurrentUser.anyUserSportProfile?.proUnlocked
    ) {
      return (
        <Navigate
          to={generateSportContextPath(ANY_SPORT_SERIES)}
          replace
          keepSearchParams
        />
      );
    }
    return (
      <Navigate
        to={generateSportContextPath(
          enableSorare27Nav ? ANY_SPORT_PLAY_BOARDS : ANY_SPORT_HOME
        )}
        replace
        keepSearchParams
      />
    );
  }

  return (
    <WalletLanding
      leaderboards={[
        So5LeaderboardType.IN_SEASON_ENGLAND_LIMITED,
        So5LeaderboardType.IN_SEASON_FRANCE_LIMITED,
        So5LeaderboardType.IN_SEASON_GERMANY_LIMITED,
      ]}
      showFAQ={!isRoot}
      faq={<FAQ />}
    />
  );
};

export default FootballPage;
